import { useEffect, useRef, useState } from 'react';
import { IconPhotoOff } from '@tabler/icons-react';
import {
  InlineError,
  Stack,
  Text,
  ThumbnailImagePicker,
  WP_IMAGE_FILE_ACCEPT,
  useDebouncedValue,
} from '@wapharma/design-system';
import type { GalleryImagePickerItem } from '@wapharma/design-system';
import { unwrapData } from '@wapharma/shared';
import { apiGet, apiPostForm } from '../api';
import { getDisplayError } from '../apiErrors';
import { parseCatalogMediaLibraryItem } from '../lib/catalogMediaLibrary';
import {
  fetchCatalogProxyImageFile,
  isAllowedImageFile,
} from '../pages/catalog/catalogPageWizardHelpers';
import styles from './MarkeLogoField.module.css';

export type MarkeLogoValue = { key: string | null; url: string | null };

export type MarkeLogoFieldProps = {
  token: string | null;
  value: MarkeLogoValue;
  onChange: (next: MarkeLogoValue) => void;
  disabled?: boolean;
  galleryItems?: GalleryImagePickerItem[];
  /** Brand name — Wikidata/Commons lookup when the field is empty / still auto-filled. */
  lookupName?: string;
  /** Compact list/edit control, large wizard stage, or beside-name inline preview. */
  presentation?: 'compact' | 'stage' | 'inline';
  /** Optional brand label shown in stage presentation (falls back to lookupName). */
  brandLabel?: string;
  /** True while upload / remote lookup runs (not name debounce — keeps footer stable while typing). */
  onBusyChange?: (busy: boolean) => void;
};

export function MarkeLogoThumb({
  url,
  name,
  size = 'sm',
}: {
  url: string | null | undefined;
  name: string;
  size?: 'sm' | 'lg';
}) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [url]);

  const sizeClass = size === 'lg' ? ` ${styles.thumbLg}` : '';
  const showImg = Boolean(url) && !broken;

  if (showImg) {
    return (
      <img
        className={`${styles.thumb}${sizeClass}`}
        src={url!}
        alt=""
        title={name}
        onError={() => setBroken(true)}
      />
    );
  }

  return (
    <span className={`${styles.thumbEmpty}${sizeClass}`} aria-hidden title={name}>
      <IconPhotoOff size={size === 'lg' ? 16 : 14} stroke={1.8} />
    </span>
  );
}

export function MarkeLogoField({
  token,
  value,
  onChange,
  disabled,
  galleryItems = [],
  lookupName,
  presentation = 'compact',
  brandLabel = '',
  onBusyChange,
}: MarkeLogoFieldProps) {
  const [uploadBusy, setUploadBusy] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const brandMode = lookupName !== undefined;
  const [ownedByUser, setOwnedByUser] = useState(
    () => !brandMode && Boolean(value.key || value.url),
  );
  const originRef = useRef<'auto' | 'user' | null>(
    brandMode ? null : value.key || value.url ? 'user' : null,
  );
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const liveName = lookupName?.trim() ?? '';
  const [debouncedName] = useDebouncedValue(liveName, 450);
  const lookupGenRef = useRef(0);
  const autoForNameRef = useRef<string | null>(null);
  const stage = presentation === 'stage';
  const inline = presentation === 'inline';
  const label = (brandLabel || liveName).trim();

  const awaitingDebounce =
    brandMode &&
    !disabled &&
    !ownedByUser &&
    liveName.length >= 2 &&
    liveName !== debouncedName;

  // Debounce alone must not flip the wizard Continuer label / disable gate every keystroke.
  const fieldBusy = uploadBusy || lookupBusy;
  const pickerBusy = fieldBusy || awaitingDebounce;

  useEffect(() => {
    onBusyChange?.(fieldBusy);
  }, [fieldBusy, onBusyChange]);

  const markUserOrigin = () => {
    originRef.current = 'user';
    setOwnedByUser(true);
    autoForNameRef.current = null;
    setLookupBusy(false);
  };

  const uploadFile = async (file: File, origin: 'auto' | 'user', isStale?: () => boolean) => {
    if (!token) {
      setErr('Non authentifié.');
      return false;
    }
    if (!isAllowedImageFile(file)) {
      setErr('Image JPEG, PNG, WebP, GIF ou AVIF.');
      return false;
    }
    setErr(null);
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const created = parseCatalogMediaLibraryItem(
        await apiPostForm<unknown>('/catalog/admin/media-library', fd, token),
      );
      if (isStale?.() || (origin === 'auto' && originRef.current === 'user')) return false;
      originRef.current = origin;
      if (origin === 'user') setOwnedByUser(true);
      onChangeRef.current({ key: created.key, url: created.url });
      return true;
    } catch (e: unknown) {
      setErr(getDisplayError(e));
      return false;
    } finally {
      setUploadBusy(false);
    }
  };

  useEffect(() => {
    if (!brandMode || !token || disabled) {
      setLookupBusy(false);
      return;
    }
    const name = debouncedName;
    if (name.length < 2) {
      if (!valueRef.current.key) originRef.current = null;
      if (originRef.current === 'auto') {
        originRef.current = null;
        autoForNameRef.current = null;
        onChangeRef.current({ key: null, url: null });
      }
      setLookupBusy(false);
      return;
    }
    if (originRef.current === 'user') {
      setLookupBusy(false);
      return;
    }
    if (originRef.current === 'auto' && autoForNameRef.current === name && valueRef.current.url) {
      setLookupBusy(false);
      return;
    }

    const gen = ++lookupGenRef.current;
    const ac = new AbortController();
    setLookupBusy(true);
    void (async () => {
      try {
        // Same path as Wa-Pharma: Wikidata/Commons via external-catalog/brand-logo.
        const raw = await apiGet<{ data?: { url: string | null } }>(
          `/catalog/admin/external-catalog/brand-logo?name=${encodeURIComponent(name)}`,
          token,
          ac.signal,
        );
        if (lookupGenRef.current !== gen) return;
        const url = unwrapData<{ url: string | null }>(raw)?.url?.trim() || null;
        if (!url) {
          if (originRef.current === 'auto' || originRef.current == null) {
            originRef.current = null;
            autoForNameRef.current = null;
            onChangeRef.current({ key: null, url: null });
          }
          return;
        }
        if (originRef.current === 'user') return;
        const file = await fetchCatalogProxyImageFile(url, token);
        if (lookupGenRef.current !== gen) return;
        if (!file) return;
        const ok = await uploadFile(file, 'auto', () => lookupGenRef.current !== gen);
        if (lookupGenRef.current !== gen) return;
        if (ok) autoForNameRef.current = name;
      } catch {
        // Abort / network — leave preview unchanged.
      } finally {
        if (lookupGenRef.current === gen) setLookupBusy(false);
      }
    })();

    return () => {
      lookupGenRef.current += 1;
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandMode, debouncedName, token, disabled]);

  const galleryOn = galleryItems.length > 0;

  const picker = (
    <ThumbnailImagePicker
      accept={WP_IMAGE_FILE_ACCEPT}
      previewUrl={value.url}
      chooseLabel="Choisir un logo"
      changeLabel="Changer le logo"
      removeLabel="Retirer le logo"
      disabled={disabled || pickerBusy || !token}
      galleryItems={galleryOn ? galleryItems : undefined}
      selectedGalleryId={galleryOn ? value.key : undefined}
      onGallerySelect={
        galleryOn
          ? (id) => {
              const hit = galleryItems.find((i) => i.id === id);
              markUserOrigin();
              onChange({ key: id, url: hit?.url ?? value.url });
            }
          : undefined
      }
      onGalleryUpload={
        galleryOn
          ? (file) => {
              markUserOrigin();
              void uploadFile(file, 'user');
            }
          : undefined
      }
      onChange={
        galleryOn
          ? undefined
          : (e) => {
              const file = e.currentTarget.files?.[0] ?? null;
              e.currentTarget.value = '';
              if (file) {
                markUserOrigin();
                void uploadFile(file, 'user');
              }
            }
      }
      onRemove={() => {
        markUserOrigin();
        setErr(null);
        onChange({ key: null, url: null });
      }}
    />
  );

  if (stage) {
    return (
      <div className={styles.stage}>
        {label ? (
          <Text size="sm" className={styles.stageBrand}>
            {label}
          </Text>
        ) : null}
        <div className={styles.stageTile}>{picker}</div>
        <Text size="sm" c="dimmed" className={styles.stageHint}>
          {fieldBusy && !value.url
            ? 'Recherche d’un logo…'
            : value.url
              ? 'Cliquez pour changer ou retirer. Suggestion auto tant que vous ne choisissez pas un fichier.'
              : 'Cliquez pour choisir un fichier, ou collez une image (Ctrl+V). Un logo peut être proposé pendant la saisie du nom.'}
        </Text>
        {err ? <InlineError>{err}</InlineError> : null}
      </div>
    );
  }

  if (inline) {
    return (
      <div className={styles.inlineWrap}>
        <div className={styles.inlinePicker}>{picker}</div>
        {err ? (
          <div className={styles.inlineMeta}>
            <InlineError>{err}</InlineError>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Stack gap="xs">
      <Text size="sm" className={styles.label}>
        Logo
      </Text>
      {picker}
      {err ? <InlineError>{err}</InlineError> : null}
    </Stack>
  );
}
